/**
 * Locale Store - Language state management
 *
 * Manages the current language and provides methods to change it.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  setLocale,
  getInitialLocale,
  LANGUAGES,
  LANGUAGE_NAMES,
  LANGUAGE_FLAGS,
  LANGUAGE_COUNTRY_CODES,
  type Language
} from '@/i18n'

export const useLocaleStore = defineStore('locale', () => {
  // State
  const currentLocale = ref<Language>(getInitialLocale())
  const isLoading = ref(false)

  // Getters
  const availableLocales = computed(() => LANGUAGES)

  const currentLanguageName = computed(() => LANGUAGE_NAMES[currentLocale.value])

  const currentLanguageFlag = computed(() => LANGUAGE_FLAGS[currentLocale.value])

  // Actions
  async function changeLocale(locale: Language) {
    if (locale === currentLocale.value) return

    isLoading.value = true

    try {
      const success = await setLocale(locale)
      if (success) {
        currentLocale.value = locale
      }
    } finally {
      isLoading.value = false
    }
  }

  function getLanguageName(locale: Language): string {
    return LANGUAGE_NAMES[locale]
  }

  function getLanguageFlag(locale: Language): string {
    return LANGUAGE_FLAGS[locale]
  }

  function getCountryCode(locale: Language): string {
    return LANGUAGE_COUNTRY_CODES[locale]
  }

  const currentCountryCode = computed(() => LANGUAGE_COUNTRY_CODES[currentLocale.value])

  return {
    // State
    currentLocale,
    isLoading,

    // Getters
    availableLocales,
    currentLanguageName,
    currentLanguageFlag,
    currentCountryCode,

    // Actions
    changeLocale,
    getLanguageName,
    getLanguageFlag,
    getCountryCode
  }
})
